/**
 * The **read-out** for one field row — the half of the row that shows the value
 * until the coordinator clicks it, at which point the grid swaps in
 * `AttributeFieldControl`.
 *
 * Split out of `attribute-grid.tsx` alongside the control, which had grown
 * past the 300-line guideline carrying the grid and both halves of the row.
 */

import { useState } from 'react';
import { CheckIcon, CopyIcon, XIcon } from 'lucide-react';
import { StatusBadge } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_HOVER,
  ATTRIBUTE_HOVER_IN_COPY_GROUP,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_RADIUS,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_SLOT,
  ATTRIBUTE_VALUE_TARGET,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import {
  ATTRIBUTE_NOT_RECORDED,
  attributePlaceholder
} from './attribute-placeholder';
import type { AttributeSpec } from './attribute-types';
import { offersCopy } from './attribute-value-cell';

/**
 * The at-rest read-out — the value, and the target the editor appears in.
 *
 * **It fills the box it is in** ({@link ATTRIBUTE_VALUE_TARGET}), which on the
 * unified path is the value cell and on the held-back one is the value column
 * itself. It hugged its own text for one release (the VALE-130 follow-up) and
 * VALE-349 reversed that: a target sized to its own string made the thing a
 * reader clicks a different size on every row. Read the token for what the hug
 * was trying to fix and where each half of it landed instead.
 */
/**
 * **Whether a value reads as prose — wrapping — rather than ellipsising.**
 *
 * Extracted from this file's own body (VALE-1740) so {@link AttributeChromeValue}
 * can ask the same question. It needs the answer for a different reason — a
 * prose value must be given the room to be typed into rather than hugging its
 * own text — and a second spelling of the rule is how the read-out and the cell
 * would come to disagree about what prose is.
 */
export function valueWraps(field: AttributeSpec<unknown>): boolean {
  return field.wraps ?? (field.fullWidth || field.editor.kind === 'address');
}

export function AttributeReadOut({
  field,
  onActivate,
  onClear,
  bare = false,
  labelId,
  pillAffordance
}: {
  field: AttributeSpec<unknown>;
  onActivate: () => void;
  /**
   * The id of the label span this row's grid rendered, so the value button can
   * carry the field's name for assistive tech (VALE-1505). Absent on chrome,
   * which has no label column to point at.
   */
  labelId?: string;
  /**
   * **The cell owns the tint and the affordances, so this renders neither**
   * (LF-6359 / [ATTR-9]).
   *
   * Set on the grid's unified path — the default since LF-6388 / [ATTR-10] —
   * where {@link AttributeValueCell} wraps the row: drawing the hover here as
   * well would tint a box *inside* the box the cell is already tinting, and
   * rendering the copy / clear buttons here would put a second pair beside the
   * cell's own.
   *
   * The `false` arm is the held-back path, which the one call site passing
   * {@link AttributeGrid}'s `legacyChrome` still takes: there this button keeps
   * the tint and the affordances, and they vanish the moment the row opens.
   * That arm is deleted when that surface converts.
   */
  bare?: boolean;
  /**
   * Commits {@link AttributeSpec.clearTo}. Absent unless the field is
   * `clearable` — see the grid, which routes it through the same commit path a
   * typed edit takes.
   */
  onClear?: () => void;
  /**
   * Rendered **inside the pill**, after the value (VALE-75 / [INVDRAW-4]).
   *
   * Only {@link AttributeChromeValue} passes it, and only for the inventory
   * drawer's header `Status`. Inside a grid a row needs no affordance at rest:
   * it sits in a column of rows that all behave the same way, and §1.2's whole
   * argument is that the record should not look like a form. A header is the
   * opposite context — one value beside a title and a delete button, with no
   * siblings to establish the convention — and LF-6314's round-6 review
   * reported exactly that: *"the user can't really know that he can click on it
   * to edit it."*
   *
   * **Inside the pill rather than beside the cell**, which round 6 tried first:
   * with no column of siblings to group things, a glyph next to the read-out
   * read as a separate control that happened to sit nearby. Inside, the pill
   * itself is the affordance.
   */
  pillAffordance?: React.ReactNode;
}) {
  const empty = field.display == null || field.display === '';
  const [copied, setCopied] = useState(false);
  /**
   * A closed vocabulary reads as a chosen state, not as typed text. A pill
   * around an email or a date is just a box around a string.
   *
   * **`boolean` earns it for a different reason than `select` does** (§1.8).
   * `select` gets the pill because the value is one of a fixed set of *states*;
   * a boolean gets it because the alternative — a box in the cell — cannot say
   * the thing that matters. *"An unticked box is indistinguishable from a
   * question nobody answered"*, whereas `Bank verified — No` states it. Both
   * land on the same badge, so a card of mixed field kinds keeps one rhythm.
   *
   * `plainValue` opts a picker out: being edited by a dropdown is not the same
   * claim as being one of a fixed set of states, and for a person or an
   * office-that-may-not-be-a-list it is the wrong one. See the prop's doc.
   *
   * **`money` is deliberately absent from the list below** — a currency figure
   * is a typed value, not a state from a closed vocabulary, so it renders
   * exactly like `text` or `number`. A pill around an amount would just be a
   * box (`components/attributes/CLAUDE.md` §3's worked example).
   *
   * **`marketFigure` is absent for the same reason** (VALE-1008), and the
   * decision is recorded rather than inherited, per §3: it is a currency figure
   * too. That its provenance is a market feed is a fact about *where it came
   * from*, which the popover states, and not about the value being one of a
   * fixed set of states.
   *
   * **`selectWithCustom` is absent, and this is the one kind where its absence
   * is the *point*** (VALE-1227). A pill claims the value is one of a fixed
   * set, and this kind exists precisely because the set is **not** fixed: its
   * whole reason for being is the reader who types a value nobody enumerated.
   * Pilling it would state the opposite of what the control was added to say,
   * and it would state it most loudly on exactly the value that is not a
   * member. The named consumer settles it independently: pronouns are a fact
   * about a person, which is the case `plainValue`'s own doc argues out of the
   * pill.
   */
  const pill =
    !empty &&
    (field.editor.kind === 'select' || field.editor.kind === 'boolean') &&
    !field.plainValue;
  /**
   * **Whether the value wraps instead of ellipsising**, and `address` derives it
   * rather than declaring it (§2 — one treatment per field type).
   *
   * `fullWidth` conflates two things: the row spanning its grid's columns, and
   * the value inside it reading as prose. That was invisible while every address
   * in the product happened to set it — and then it was not: the enquiry
   * drawer's `Address` row (`lead-field-specs.ts`) never did, so the same field,
   * on a surface an inch from the Contacts one it is parity-tested against,
   * rendered `12 Example St, Syd…`. A truncated address is the exact defect
   * `fullWidth`'s own doc describes, and it does not become acceptable because a
   * call site forgot the prop.
   *
   * So the *span* stays declared — which fields deserve a whole row is a layout
   * decision a surface makes — and the *wrap* is derived, which frees a row to
   * be one without the other. The matter Overview's estate card is the first to
   * want that: its residential address sits in a third of the card and still
   * has to be readable (VALE-130 follow-up).
   */
  const wraps = valueWraps(field);

  /*
    A `<span>` for a read-only row, a `<button>` for every other — the element
    itself carries the claim. A button that ignored its own click, or one left
    focusable with nothing behind it, would say *editable* to a pointer and to a
    screen reader alike and then not be; the tag is the honest version, and it
    takes the Tab stop and the `cursor-pointer` with it.
  */
  const ValueTag = field.readOnly ? 'span' : 'button';

  /*
    A second, smaller fact beside the value (VALE-166) — the `Deceased` row's
    date of death. Suppressed on an empty row: a chip qualifying a
    `Set deceased…` placeholder would be qualifying nothing, and the row's own
    empty state is the message.

    It is deliberately *inside* the value target rather than a sibling of it.
    On the unified path this component returns `valueButton` alone (`bare`), so
    anything rendered beside it would simply not appear on the surface that
    asked for it — and a separate box in a row whose whole cell opens one editor
    would read as a second control.
  */
  const badge = !empty && field.badge != null ? field.badge : null;

  /** {@link AttributeSpec.markWrapper}, defaulted to the identity. */
  const wrapMark = field.markWrapper ?? ((mark: React.ReactNode) => mark);

  const valueButton = (
    <ValueTag
      type={field.readOnly ? undefined : 'button'}
      /*
        The row's label, as the button's DESCRIPTION (VALE-1505). At rest the
        button's only content is the value, `Miller`, or `Set date of
        birth…`, so a screen reader announced "Miller, button" with no field
        name; every *open* editor already carries `aria-label={field.label}`, so
        the row was named only while being edited. `aria-describedby` reads the
        label after the value ("Miller, button, First name") and leaves the
        accessible NAME as the value, which is what the label column's
        `getByLabelText` queries and every existing spec address the row by.
        Only on the button: a read-only span is not a focus stop.
      */
      aria-describedby={!field.readOnly && labelId ? labelId : undefined}
      onClick={field.readOnly ? undefined : onActivate}
      /*
        The value itself, because the cell truncates long addresses and emails
        and the full string has to live somewhere. Nothing when empty: the
        placeholder already says `Set <label>…`, so a tooltip would only repeat
        the text under the pointer.
      */
      title={
        typeof field.display === 'string' && field.display
          ? field.display
          : undefined
      }
      className={cn(
        // The caller's own classes for the value (VALE-1208), the AI-extract
        // `leading-5` + `py-1` fix the highlight at 28px whether the value is
        // plain text (13px) or a pill (12px in a 20px chip), so a card of mixed
        // field kinds keeps one rhythm.
        // `slate-50`, not the `slate-100` this surface uses elsewhere: the
        // `StatusBadge` a `select` row renders is `neutral`, whose fill
        // (`bg-surface-subtle`) IS this cell's own hover highlight — without
        // the border, the pill would vanish entirely on the one interaction
        // that is supposed to reveal the row. Verified by screenshot, not by
        // eye-balling the hex.
        //
        // Every `StatusBadge` renders bordered now (VALE-1725, retiring the
        // `bordered`/`subtle` emphasis split this comment used to describe):
        // the pill's own `border-border` edge is what keeps it visible
        // against this highlight, not a differently-tinted fill.
        // `group-hover/copy` alongside the plain `hover`: on a copyable row the
        // copy icon is a sibling, so moving the pointer onto it leaves this
        // button and would drop the highlight at the exact moment the row is
        // most active. The group covers the pair. Rows with no copy affordance
        // never open that group, so the variant simply never matches there.
        //
        // The target fills its box rather than hugging its own text — see
        // `ATTRIBUTE_VALUE_TARGET` (VALE-349). On the unified path that box is
        // the cell, so the tint, the affordances and the hit area are one box
        // the width of the value column; on the held-back `legacyChrome` path
        // this button *is* that box.
        ATTRIBUTE_VALUE_TARGET,
        'text-left transition-colors',
        // No pointer, and no tint below, on a row that does not open.
        !field.readOnly && 'cursor-pointer',
        ATTRIBUTE_RADIUS,
        ATTRIBUTE_INSET,
        ATTRIBUTE_LEADING,
        // Both dropped on the refined path: the cell draws the tint, and a
        // second one inside it would be a box within a box.
        !bare && !field.readOnly && ATTRIBUTE_HOVER_IN_COPY_GROUP,
        !bare && !field.readOnly && ATTRIBUTE_HOVER,
        ATTRIBUTE_VALUE_TEXT,
        empty
          ? // Grey placeholder rather than the dashed `Add` pill (LF-6099): it
            // names the field it would fill, which `Add` never did.
            cn('truncate', ATTRIBUTE_MUTED_INK)
          : ATTRIBUTE_VALUE_INK,
        !empty &&
          (pill || badge
            ? // A badge makes the target two boxes, so it lays out as a row and
              // the *value* takes the truncation — see the badge span below.
              'flex items-center'
            : wraps
              ? // Wraps and reads left-to-right like the prose it is;
                // `break-words` because a pasted reference or email can be one
                // unbreakable token wider than the card.
                'break-words whitespace-pre-line'
              : 'truncate'),
        // The caller's classes for the VALUE (VALE-1208), last so they win.
        field.valueClassName
      )}
    >
      {/*
        `markWrapper` wraps whatever this button DREW, and stays inside it
        (VALE-1264) — see the prop. `valueWrapper` sits outside the button, so a
        caller hanging a hover popover off that one shrink-wrapped the button to
        its own contents and collapsed the row's click target, while the cell's
        hover tint went on promising the whole value column.

        Every branch, not just the pill: a row drawing the grey `Set …`
        placeholder is exactly the one whose caller most needs the wrapper, and
        it never reaches the pill branch at all.
      */}
      {wrapMark(
        empty ? (
          field.readOnly ? (
            ATTRIBUTE_NOT_RECORDED
          ) : (
            attributePlaceholder(field.label)
          )
        ) : pill ? (
          /*
          The pill sets its own ink, so the caller's `valueClassName` is
          restated ON it, on the cell alone it never reached the value the
          reader sees (VALE-1208).
        */
          <StatusBadge className={cn('min-w-0', field.valueClassName)}>
            <span className="truncate">{field.display}</span>
            {pillAffordance}
          </StatusBadge>
        ) : badge ? (
          <>
            {/*
            The value truncates and the badge does not: the badge is short and
            fixed-shape (a date, a count), while the value is a name that can be
            any length. Letting the badge shrink instead would ellipsise the
            thing that is already too small to lose characters from.
          */}
            <span className="min-w-0 truncate">{field.display}</span>
            {/*
            **The neutral chip is for a badge that is plain TEXT** (VALE-1247).

            `badge` is a `ReactNode`, and this wrapped every one of them — so a
            caller handing over presentation of its own got it wrapped in a
            second, neutral chip. Three of the five call sites do exactly that
            (the bank account's verified mark, `ChangeTag` on both review
            surfaces, `PlanningProvenanceBadge`), and the result was a green or
            amber chip sitting inside a grey one, which reads as a rendering
            fault rather than as a badge.

            The two callers that pass a bare string — the deceased row's date of
            death, the inventory figure's `Manually set` — have no presentation
            of their own and are exactly what the chip was written for, so they
            keep it. A node draws itself; text gets dressed.
          */}
            {typeof badge === 'string' || typeof badge === 'number' ? (
              <StatusBadge className="ml-1.5 shrink-0">{badge}</StatusBadge>
            ) : (
              <span className="ml-1.5 flex shrink-0 items-center self-center leading-none">
                {badge}
              </span>
            )}
          </>
        ) : (
          field.display
        )
      )}
    </ValueTag>
  );

  const copyText =
    field.copyText ?? (typeof field.draft === 'string' ? field.draft : '');
  // `offersCopy`, not `field.copyable`: the held-back `legacyChrome` path draws
  // its own affordances, and reading the flag directly is how it would come to
  // answer differently from the cell for the same field.
  const showCopy = offersCopy(field) && !empty && Boolean(copyText);
  /*
    Nothing to clear on an already-empty field: the affordance would be a `×`
    beside the `Set <label>…` placeholder it is meant to produce.
  */
  const showClear = Boolean(onClear) && !empty;
  // `bare` first: on the refined path the cell renders both affordances, so this
  // must return the value button alone even when the spec is copyable.
  /*
    `valueWrapper` wraps whatever this row drew (VALE-1208), the review hangs a
    hover popover off a changed value, and a `select` draws a pill rather than
    text, so there is nothing inside `display` to hang it on.
  */
  const wrapped = field.valueWrapper
    ? field.valueWrapper(valueButton)
    : valueButton;

  if (bare || (!showCopy && !showClear)) return wrapped;

  const copy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable outside a secure context. Silent by design:
      // the coordinator can still select the value, and a toast for a browser
      // capability she cannot change would be noise on every click.
    }
  };

  /*
    One reveal rule for both affordances, so a row carrying copy *and* clear
    shows them together rather than fading in as two separate gestures.
  */
  const revealed =
    'hidden shrink-0 rounded p-0.5 text-foreground-subtle transition-colors group-focus-within/copy:inline-flex group-hover/copy:inline-flex hover:bg-muted hover:text-foreground-secondary';

  return (
    /*
      The copy affordance sits *after* the value, not before it: it used to lead
      a right-aligned value, and leading a left-aligned one would put an icon
      between the label and the value column's left edge.

      **It is `hidden` until the row is hovered, and that is a layout decision as
      much as a visual one.** Rendered permanently it charged every copyable row
      ~21px of value width for an affordance nobody is reaching for most of the
      time — on a narrow row that is the difference between reading an email and
      reading `priya.r…`. `hidden` (`display: none`) rather than `opacity-0`
      precisely because opacity would still occupy the space and change nothing.

      So the value gets the whole cell at rest, and the icon appearing on hover
      re-flows the row and briefly shortens the value instead. That is the
      intended behaviour, not a side effect: the reader has the full string until
      the moment they gesture at copying it, and pointing at a value is not how
      you read it.

      `group-focus-within` keeps it reachable without a pointer. `display: none`
      is unfocusable, so the button cannot reveal itself — but tabbing to the
      *value* puts focus inside the group, which shows the icon, and the next Tab
      lands on it.
    */
    <span className={cn('group/copy items-center gap-1', ATTRIBUTE_VALUE_SLOT)}>
      {wrapped}
      {showCopy ? (
        <button
          type="button"
          onClick={copy}
          title={copied ? 'Copied' : `Copy ${field.label.toLowerCase()}`}
          // Named for its state, matching the value cell — see the note there.
          // The two paths must not drift on this while both are alive.
          aria-label={
            copied
              ? `Copied ${field.label.toLowerCase()}`
              : `Copy ${field.label.toLowerCase()}`
          }
          className={revealed}
        >
          {copied ? (
            <CheckIcon className="text-success-subtle-foreground h-3 w-3" />
          ) : (
            <CopyIcon className="h-3 w-3" />
          )}
        </button>
      ) : null}
      {showClear ? (
        /*
          Clearing a picker or a date otherwise means opening the control and
          hunting for a `Not recorded` sentinel — one this grid literally ships
          (`NONE = '__none'` in `./lead-field-specs`) because a Radix
          `SelectItem` cannot hold an empty value. A `×` on the row is the
          direct gesture for the same intent.

          `onClick`, deliberately not `onMouseDown`: it must not fire while the
          pointer is merely crossing the row on its way somewhere else.

          `stopPropagation` for the same reason the copy button does it — the
          value button beside it opens the editor, and clearing a field must not
          also leave an editor open over the value it just emptied.
        */
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClear?.();
          }}
          title={`Clear ${field.label.toLowerCase()}`}
          aria-label={`Clear ${field.label.toLowerCase()}`}
          className={revealed}
        >
          <XIcon className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}
